import React, { useEffect, useState, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Select,
  IconButton,
  Spinner,
  Avatar,
  Divider,
  useToast,
} from '@chakra-ui/react'
import { FiTrash2, FiUsers, FiShare2, FiLink } from 'react-icons/fi'
import { useAppDispatch, useAppSelector } from '@/store'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useOrgData } from '@/contexts/OrgDataContext'
import { useAuth } from '@/contexts/AuthContext'
import {
  fetchFilePermissions,
  grantFilePermission,
  revokeFilePermission,
} from '@/store/slices/filePermissionsSlice'
import { FilePermission, FilePermissionLevel } from '@/types'

/** Stable fallback so useSelector does not see a new [] reference when data is missing. */
const EMPTY_FILE_PERMISSIONS: FilePermission[] = []

interface Props {
  isOpen: boolean
  onClose: () => void
  fileId: number
  fileName: string
}

const permissionColors: Record<FilePermissionLevel, string> = {
  viewer: 'gray',
  editor: 'blue',
}

export default function FilePermissionsModal({ isOpen, onClose, fileId, fileName }: Props) {
  const { t } = useAppTranslation(['files', 'common'])
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { user } = useAuth()

  const permissions = useAppSelector(
    (state) => state.filePermissions.byFileId[fileId] ?? EMPTY_FILE_PERMISSIONS
  )
  const isLoading = useAppSelector((state) => state.filePermissions.isLoading[fileId] || false)
  const { orgUsers, groups } = useOrgData()

  const [addType, setAddType] = useState<'user' | 'group'>('user')
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [selectedPermission, setSelectedPermission] = useState<FilePermissionLevel>('viewer')
  const [isAdding, setIsAdding] = useState(false)

  const userLookup = useMemo(() => {
    const map = new Map<number, string>()
    orgUsers.forEach((u) => map.set(u.id, u.name))
    return map
  }, [orgUsers])

  const groupLookup = useMemo(() => {
    const map = new Map<number, string>()
    groups.forEach((g) => map.set(g.id, g.name))
    return map
  }, [groups])

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchFilePermissions(fileId))
    }
  }, [isOpen, fileId, dispatch])

  const handleGrant = async () => {
    if (addType === 'user' && !selectedUserId) return
    if (addType === 'group' && !selectedGroupId) return

    setIsAdding(true)
    try {
      await dispatch(
        grantFilePermission({
          fileId,
          userId: addType === 'user' ? Number(selectedUserId) : undefined,
          groupId: addType === 'group' ? Number(selectedGroupId) : undefined,
          permission: selectedPermission,
        })
      ).unwrap()
      toast({
        title: t('filePermissions.grantSuccess', { ns: 'files' }),
        status: 'success',
        duration: 2000,
      })
      setSelectedUserId('')
      setSelectedGroupId('')
    } catch {
      toast({
        title: t('filePermissions.grantError', { ns: 'files' }),
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsAdding(false)
    }
  }

  const handleRevoke = async (userId?: number, groupId?: number) => {
    try {
      await dispatch(revokeFilePermission({ fileId, userId, groupId })).unwrap()
      toast({
        title: t('filePermissions.revokeSuccess', { ns: 'files' }),
        status: 'success',
        duration: 2000,
      })
    } catch {
      toast({
        title: t('filePermissions.revokeError', { ns: 'files' }),
        status: 'error',
        duration: 3000,
      })
    }
  }

  const availableUsers = orgUsers.filter((u) => u.id !== user?.id && !permissions.some((p) => p.user_id === u.id))
  const availableGroups = groups.filter((g) => !permissions.some((p) => p.group_id === g.id))

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="brand.600"
        >
          <HStack spacing={2}>
            <Box as={FiShare2 as any} />
            <Text>{t('filePermissions.title', { ns: 'files' })} — {fileName}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Current permissions */}
            <Text fontWeight="semibold" fontSize="sm">
              {t('filePermissions.currentPermissions', { ns: 'files' })}
            </Text>
            {isLoading ? (
              <Spinner size="sm" />
            ) : permissions.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                {t('filePermissions.noPermissions', { ns: 'files' })}
              </Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {permissions.map((perm) => (
                  <HStack
                    key={perm.id}
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg="gray.50"
                    _dark={{ bg: 'gray.700' }}
                  >
                    <HStack spacing={2}>
                      {perm.user_id ? (
                        <Avatar size="xs" name={perm.user?.name || userLookup.get(perm.user_id)} />
                      ) : (
                        <Box as={FiUsers as any} />
                      )}
                      <Text fontSize="sm">
                        {perm.user?.name
                          || (perm.user_id ? userLookup.get(perm.user_id) : null)
                          || perm.group?.name
                          || (perm.group_id ? groupLookup.get(perm.group_id) : null)
                          || 'Unknown'}
                      </Text>
                    </HStack>
                    <HStack>
                      <Badge colorScheme={permissionColors[perm.permission]}>
                        {t(`filePermissions.${perm.permission}`, { ns: 'files' })}
                      </Badge>
                      <IconButton
                        aria-label={t('filePermissions.removeAccess', { ns: 'files' })}
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() =>
                          handleRevoke(perm.user_id ?? undefined, perm.group_id ?? undefined)
                        }
                      />
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            )}

            <Divider />

            {/* Add section */}
            <Text fontWeight="semibold" fontSize="sm">
              {t('filePermissions.addPeople', { ns: 'files' })}
            </Text>
            <Select
              size="sm"
              value={addType}
              onChange={(e) => {
                setAddType(e.target.value as 'user' | 'group')
                setSelectedUserId('')
                setSelectedGroupId('')
              }}
            >
              <option value="user">{t('filePermissions.user', { ns: 'files' })}</option>
              <option value="group">{t('filePermissions.group', { ns: 'files' })}</option>
            </Select>
            {addType === 'group' ? (
              <Select
                size="sm"
                placeholder={t('filePermissions.searchPlaceholder', { ns: 'files' })}
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
              >
                {availableGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Select
                size="sm"
                placeholder={t('filePermissions.searchPlaceholder', { ns: 'files' })}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </Select>
            )}
            <Box>
              <Select
                size="sm"
                value={selectedPermission}
                onChange={(e) => setSelectedPermission(e.target.value as FilePermissionLevel)}
              >
                <option value="viewer">{t('filePermissions.viewer', { ns: 'files' })}</option>
                <option value="editor">{t('filePermissions.editor', { ns: 'files' })}</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {t(`filePermissions.${selectedPermission}Description`, { ns: 'files' })}
              </Text>
            </Box>
            <Button
              colorScheme="brand"
              size="sm"
              onClick={handleGrant}
              isLoading={isAdding}
              isDisabled={addType === 'user' ? !selectedUserId : !selectedGroupId}
            >
              {t('filePermissions.addAccess', { ns: 'files' })}
            </Button>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FiLink />}
            color="gray.500"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href).then(() => {
                toast({ title: 'Link copied to clipboard', status: 'success', duration: 2000, isClosable: true })
              })
            }}
          >
            Copy link
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('buttons.close', { ns: 'common' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
